// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'
import { impresoraFragmentAst } from '../fragments/impresoraFragment.ts'
import { ImpresoraProps } from '../interfaces/impresora.ts'

const query = /* GraphQL */ gqlAst`
  ${impresoraFragmentAst}

  query IMPRESORA_POR_ID($id: ID!) {
    impresoraPorId(id: $id) {
      ...impresoraFields
    }
  }
`

export const KEY_IMPRESORA_POR_ID = 'impresora-por-id'

/**
 * Servicio para obtener una impresora por su ID.
 * @param id Id de la impresora.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @author isi-template
 */
export const apiImpresoraPorId = async (
  id: string,
  options: GraphqlClientOptionsProps = {},
): Promise<ImpresoraProps> => {
  try {
    const data = await graphqlClient.request<{ impresoraPorId: ImpresoraProps }>({
      document: query,
      variables: { id },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraPorId
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida el detalle de una impresora desde cualquier lugar (ej. onSuccess de mutaciones),
 * sin necesidad de tener montado el hook. Sin `id`, invalida el detalle de todas las impresoras.
 * @example invalidateImpresoraPorId(impresora._id)
 * @author isi-template
 */
export const invalidateImpresoraPorId = (
  id?: string,
  options?: Omit<InvalidateQueryOptions, 'extraQueryKeys'>,
) => invalidateQuery(KEY_IMPRESORA_POR_ID, { ...options, extraQueryKeys: id ? [id] : [] })

/**
 * Hook para consultar una impresora por su ID. Solo consulta si hay `id`.
 * queryKey: [KEY_IMPRESORA_POR_ID, id, ...(options.queryKeys ?? [])]
 * Para invalidar sin montar el hook, usar `invalidateImpresoraPorId(id)`.
 * @param id Id de la impresora.
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 * @author isi-template
 */
export const useImpresoraPorId = (id: string | null | undefined, options: QueryOpts<ImpresoraProps> = {}) =>
  useApiQuery(KEY_IMPRESORA_POR_ID, (ctx) => apiImpresoraPorId(id!, { signal: ctx.signal }), {
    ...options,
    enabled: !!id && (options.enabled ?? true),
    queryKeys: [id, ...(options.queryKeys ?? [])],
  })
